import { useState, useRef, useEffect } from "react";
const mockEmployees = [
  { id: 1, name: "Ravi Teja", role: "Field Surveyor" },
  { id: 2, name: "Suresh Kumar", role: "Field Surveyor" },
  { id: 3, name: "Kiran Babu", role: "Lab Analyst" },
  { id: 4, name: "Naresh Yadav", role: "CAD Engineer" },
  { id: 5, name: "Priya Sharma", role: "Survey Manager" },
];

const JOB_STATUSES = ["Pending", "In Progress", "Completed", "Delivered"];

interface BulkActionBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkStatusChange: (status: string) => void;
  onBulkAssign: (employee: string) => void;
  onBulkExport: (format: "csv" | "excel" | "pdf") => void;
  onSelectAll: () => void;
  totalCount: number;
}

export default function BulkActionBar({
  selectedCount,
  onClearSelection,
  onBulkStatusChange,
  onBulkAssign,
  onBulkExport,
  onSelectAll,
  totalCount,
}: BulkActionBarProps) {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [assignMenuOpen, setAssignMenuOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ type: "status" | "assign"; value: string } | null>(null);

  const statusRef = useRef<HTMLDivElement>(null);
  const assignRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) setStatusMenuOpen(false);
      if (assignRef.current && !assignRef.current.contains(e.target as Node)) setAssignMenuOpen(false);
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) setExportMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleStatusSelect = (status: string) => {
    setStatusMenuOpen(false);
    setConfirmModal({ type: "status", value: status });
  };

  const handleAssignSelect = (emp: string) => {
    setAssignMenuOpen(false);
    setConfirmModal({ type: "assign", value: emp });
  };

  const handleConfirm = () => {
    if (!confirmModal) return;
    if (confirmModal.type === "status") onBulkStatusChange(confirmModal.value);
    else onBulkAssign(confirmModal.value);
    setConfirmModal(null);
  };

  return (
    <>
      {/* Bulk Action Bar */}
      <div className="flex items-center gap-3 px-4 py-3 bg-amber-600 rounded-xl text-white animate-in slide-in-from-top-2 duration-200">
        {/* Selection info */}
        <div className="flex items-center gap-3 flex-1">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center bg-white/20 rounded">
              <i className="ri-checkbox-line text-sm" />
            </div>
            <span className="text-sm font-semibold">
              {selectedCount} selected
            </span>
          </div>
          <button
            onClick={onSelectAll}
            className="text-xs text-amber-200 hover:text-white underline cursor-pointer whitespace-nowrap transition-colors"
          >
            Select all {totalCount}
          </button>
          <button
            onClick={onClearSelection}
            className="text-xs text-amber-200 hover:text-white cursor-pointer whitespace-nowrap transition-colors"
          >
            Clear
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Update Status */}
          <div className="relative" ref={statusRef}>
            <button
              onClick={() => { setStatusMenuOpen((v) => !v); setAssignMenuOpen(false); setExportMenuOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-sm" /></span>
              Update Status
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${statusMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {statusMenuOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-xl border border-gray-100 overflow-hidden z-30" style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
                {JOB_STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => handleStatusSelect(s)}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 cursor-pointer transition-colors text-left"
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      s === "Pending" ? "bg-amber-400" :
                      s === "In Progress" ? "bg-blue-400" :
                      s === "Completed" ? "bg-amber-400" : "bg-purple-400"
                    }`} />
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Assign Employee */}
          <div className="relative" ref={assignRef}>
            <button
              onClick={() => { setAssignMenuOpen((v) => !v); setStatusMenuOpen(false); setExportMenuOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></span>
              Assign
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${assignMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {assignMenuOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-52 bg-white rounded-xl border border-gray-100 overflow-hidden z-30" style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Assign to Employee</p>
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {mockEmployees.map((emp) => (
                    <button
                      key={emp.id}
                      onClick={() => handleAssignSelect(emp.name)}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-amber-50 cursor-pointer transition-colors text-left"
                    >
                      <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {emp.name[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{emp.name}</p>
                        <p className="text-xs text-gray-400 truncate">{emp.role}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Export */}
          <div className="relative" ref={exportRef}>
            <button
              onClick={() => { setExportMenuOpen((v) => !v); setStatusMenuOpen(false); setAssignMenuOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-download-2-line text-sm" /></span>
              Export
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${exportMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-40 bg-white rounded-xl border border-gray-100 overflow-hidden z-30" style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
                <button onClick={() => { onBulkExport("csv"); setExportMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                  <i className="ri-file-list-3-line text-gray-400" /> CSV
                </button>
                <button onClick={() => { onBulkExport("excel"); setExportMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                  <i className="ri-file-excel-line text-amber-500" /> Excel
                </button>
                <button onClick={() => { onBulkExport("pdf"); setExportMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                  <i className="ri-file-pdf-line text-red-500" /> PDF
                </button>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="w-px h-5 bg-white/20" />

          {/* Close */}
          <button
            onClick={onClearSelection}
            className="w-7 h-7 flex items-center justify-center bg-white/15 hover:bg-white/25 rounded-lg cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      </div>

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setConfirmModal(null)}>
          <div className="bg-white rounded-2xl border border-gray-100 p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${confirmModal.type === "status" ? "bg-amber-50" : "bg-amber-50"}`}>
                <i className={`text-xl ${confirmModal.type === "status" ? "ri-refresh-line text-amber-600" : "ri-user-add-line text-amber-600"}`} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">
                  {confirmModal.type === "status" ? "Update Status" : "Assign Employee"}
                </h3>
                <p className="text-xs text-gray-500">
                  {selectedCount} job card{selectedCount > 1 ? "s" : ""} selected
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 mb-5">
              <p className="text-sm text-gray-600">
                {confirmModal.type === "status" ? (
                  <>Set status to <strong className="text-gray-900">&quot;{confirmModal.value}&quot;</strong> for {selectedCount} job card{selectedCount > 1 ? "s" : ""}?</>
                ) : (
                  <>Assign <strong className="text-gray-900">{confirmModal.value}</strong> to {selectedCount} job card{selectedCount > 1 ? "s" : ""}?</>
                )}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
